/**
 * FinancialsPage — overview of revenue, costs, pipeline, and per-project
 * effective hourly rate.
 *
 * Sources: GET /api/financials/summary  (aggregated by the backend).
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend,
} from "recharts";
import {
  ArrowsClockwise, ChartLine, CurrencyDollar, Warning, Eye, EyeSlash,
  PencilSimple, Sparkle, Receipt,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import {
  getFinancialsSummary, setProjectHours, reEstimateHours,
  getProject, updateProject,
} from "../api/projects";
import { useFinancialsVisible } from "../hooks/useFinancialsVisible";
import CostEditModal from "../components/projects/CostEditModal";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtAUD = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 })
    .format(typeof n === "number" ? n : 0);

const fmtAUDExact = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" })
    .format(typeof n === "number" ? n : 0);

const fmtMonth = (iso) => {
  // iso = "YYYY-MM"
  const [y, m] = (iso || "").split("-");
  if (!y || !m) return iso;
  return new Date(`${y}-${m}-01`).toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
};

/** Colour bucket for effective hourly rate (Net ÷ Hours).
 *  < $20 → red, $20-40 → amber, > $40 → emerald. */
function rateBucket(rate) {
  if (rate == null) return { bg: "bg-slate-100", text: "text-slate-500", label: "—" };
  if (rate < 20)   return { bg: "bg-rose-100",    text: "text-rose-700",    label: "low" };
  if (rate < 40)   return { bg: "bg-amber-100",   text: "text-amber-700",   label: "mid" };
  return                  { bg: "bg-emerald-100", text: "text-emerald-700", label: "good" };
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, hidden, accent }) {
  const accents = {
    blue:    "text-blue-600",
    emerald: "text-emerald-600",
    violet:  "text-violet-600",
    amber:   "text-amber-600",
  };
  return (
    <div className="bg-white border border-slate-200 px-5 py-4">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`font-cabinet text-2xl font-black tracking-tight mt-1 ${accents[accent] || "text-slate-900"}`}>
        {hidden ? <span className="tracking-widest text-slate-400 select-none">••••••</span> : value}
      </p>
      <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}

// ─── Editable Hours Cell ──────────────────────────────────────────────────────

function HoursCell({ row, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.estimated_hours ?? "");
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    const n = parseFloat(value);
    if (!Number.isFinite(n) || n < 0) {
      setEditing(false);
      setValue(row.estimated_hours ?? "");
      return;
    }
    setSaving(true);
    try {
      const updated = await setProjectHours(row.id, n);
      onUpdated(updated);
      toast.success(`Hours saved for "${row.name}"`);
    } catch (e) {
      toast.error(`Failed to save hours: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        step="0.5"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onWheel={(e) => e.target.blur()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setEditing(false); setValue(row.estimated_hours ?? ""); }
        }}
        disabled={saving}
        className="w-20 px-2 py-1 text-sm border border-primary rounded-sm focus:outline-none"
        data-testid={`hours-input-${row.id}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1 group hover:bg-slate-50 px-2 py-1 -mx-2 rounded transition-colors"
      title={row.estimated_hours_manual ? "Set by you — click to edit" : "AI-generated estimate — click to edit or override"}
      data-testid={`hours-cell-${row.id}`}
    >
      <span className="font-medium tabular-nums text-slate-800">
        {row.estimated_hours != null ? `${Number(row.estimated_hours).toFixed(1)}` : "—"}
      </span>
      {row.estimated_hours != null && (
        row.estimated_hours_manual
          ? <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-violet-50 text-violet-600 text-[9px] font-semibold rounded">You</span>
          : <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-semibold rounded"><Sparkle size={8} weight="fill" />AI</span>
      )}
    </button>
  );
}

// ─── Past Projects Table ──────────────────────────────────────────────────────

function PastProjectsTable({ rows, hidden, onUpdated, onEditCosts }) {
  const navigate = useNavigate();
  const masked = <span className="tracking-widest text-slate-400 select-none">••••</span>;

  if (rows.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
        <Receipt size={26} className="text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">No completed projects yet.</p>
        <p className="text-xs text-slate-400 mt-1">Mark a project as <strong>Completed</strong> from its detail page to see it here.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="past-projects-table">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
        <Receipt size={14} className="text-slate-500" weight="fill" />
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Past Projects</h3>
        <span className="text-[10px] text-slate-400 ml-1">({rows.length})</span>
        <div className="ml-auto inline-flex items-center gap-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
          <Warning size={11} weight="fill" /> Hourly rate is a very rough estimate
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <th className="text-left  px-4 py-2">Project</th>
              <th className="text-left  px-4 py-2 w-28">Completed</th>
              <th className="text-right px-4 py-2 w-28">Quote</th>
              <th className="text-right px-4 py-2 w-28">Costs</th>
              <th className="text-right px-4 py-2 w-28">Invoice</th>
              <th className="text-right px-4 py-2 w-28">Net</th>
              <th className="text-right px-4 py-2 w-24">Hours</th>
              <th className="text-right px-4 py-2 w-28">$/hr</th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const bucket = rateBucket(row.hourly_rate);
              return (
                <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`past-project-row-${row.id}`}>
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => navigate(`/projects/${row.id}`)}
                      className="text-slate-800 hover:text-primary hover:underline text-left font-medium truncate max-w-[260px]"
                      data-testid={`past-project-link-${row.id}`}
                      title={row.name}
                    >
                      {row.name}
                    </button>
                    {row.estimated_hours_reasoning && !row.estimated_hours_manual && (
                      <p className="text-[10px] text-slate-400 italic mt-0.5 truncate max-w-[300px]" title={row.estimated_hours_reasoning}>
                        AI: {row.estimated_hours_reasoning}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">
                    {row.completed_at ? new Date(row.completed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" }) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                    {hidden ? masked : (row.quoted_price != null ? fmtAUD(row.quoted_price) : "—")}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">
                    {hidden ? masked : fmtAUD(row.total_costs)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-800 font-medium">
                    {hidden ? masked : (row.final_invoice_amount != null ? fmtAUD(row.final_invoice_amount) : "—")}
                  </td>
                  <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${
                    hidden ? "" : (row.net_profit >= 0 ? "text-emerald-700" : "text-rose-700")
                  }`}>
                    {hidden ? masked : fmtAUD(row.net_profit)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <HoursCell row={row} onUpdated={onUpdated} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {row.hourly_rate == null ? (
                      <span className="text-slate-300 text-xs">—</span>
                    ) : hidden ? (
                      masked
                    ) : (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold tabular-nums ${bucket.bg} ${bucket.text}`}
                        data-testid={`hourly-rate-${row.id}`}
                      >
                        {fmtAUDExact(row.hourly_rate)}/h
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => onEditCosts(row)}
                      className="text-slate-400 hover:text-primary"
                      title="Edit cost breakdown"
                      data-testid={`edit-costs-btn-${row.id}`}
                    >
                      <PencilSimple size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FinancialsPage() {
  const { hidden: financialsHidden, toggle: toggleFinancials } = useFinancialsVisible();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [editing, setEditing] = useState(null);   // a project being cost-edited

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const summary = await getFinancialsSummary();
      setData(summary);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load financials");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh a single row when the user edits hours
  const handleHoursUpdated = useCallback((updated) => {
    setData((prev) => {
      if (!prev) return prev;
      const totalCosts = (updated.costs || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);
      const net = (Number(updated.final_invoice_amount) || 0) - totalCosts;
      const hours = Number(updated.estimated_hours) || 0;
      const newRow = {
        id:                       updated.id,
        name:                     updated.name,
        completed_at:             updated.completed_at,
        quoted_price:             updated.quoted_price,
        final_invoice_amount:     updated.final_invoice_amount,
        total_costs:              totalCosts,
        net_profit:               net,
        estimated_hours:          updated.estimated_hours,
        estimated_hours_manual:   updated.estimated_hours_manual,
        estimated_hours_reasoning: updated.estimated_hours_reasoning,
        hourly_rate:              hours > 0 ? net / hours : null,
      };
      return {
        ...prev,
        completed: prev.completed.map((r) => r.id === newRow.id ? newRow : r),
      };
    });
  }, []);

  const handleOpenCostEdit = useCallback(async (row) => {
    try {
      const project = await getProject(row.id);
      setEditing(project);
    } catch {
      toast.error("Failed to load project for editing.");
    }
  }, []);

  const handleSaveCosts = useCallback(async (costs) => {
    if (!editing) return;
    try {
      const updated = await updateProject(editing.id, { costs });
      handleHoursUpdated(updated);
      toast.success("Cost breakdown updated.");
      setEditing(null);
    } catch (e) {
      toast.error(`Failed to update costs: ${e.response?.data?.detail || e.message}`);
    }
  }, [editing, handleHoursUpdated]);

  const handleReEstimate = useCallback(async (row) => {
    try {
      await reEstimateHours(row.id);
      toast.info(`Re-estimating hours for "${row.name}"…`);
      // The estimate runs in a background task — poll once after a few seconds.
      setTimeout(load, 6000);
    } catch (e) {
      toast.error(`Failed to re-trigger AI: ${e.response?.data?.detail || e.message}`);
    }
  }, [load]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex-1 overflow-y-auto bg-slate-50" data-testid="financials-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-8 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2">
              <ChartLine size={20} className="text-emerald-500" weight="fill" /> Financials
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Revenue trends, pipeline, and effective hourly rate across all projects.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button" variant="outline" size="sm"
              onClick={toggleFinancials}
              className="text-slate-600"
              data-testid="toggle-financials-financials-page"
              title={financialsHidden ? "Show amounts" : "Hide amounts"}
            >
              {financialsHidden
                ? <><EyeSlash size={13} className="mr-1.5" weight="bold" /> Show $</>
                : <><Eye      size={13} className="mr-1.5" weight="bold" /> Hide $</>}
            </Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-financials-btn">
              <ArrowsClockwise size={13} className={loading ? "animate-spin mr-1.5" : "mr-1.5"} weight="bold" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-6 max-w-screen-2xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3">
            <ArrowsClockwise size={22} className="text-primary animate-spin" />
            <p className="text-sm text-slate-500">Loading financials…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <Warning size={26} className="text-red-400" />
            <p className="text-sm text-slate-600">{error}</p>
            <Button size="sm" variant="outline" onClick={load}>Retry</Button>
          </div>
        ) : data ? (
          <>
            {/* Stats grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="financials-stats">
              <StatCard
                label="Avg / Month Invoiced"
                value={fmtAUD(data.stats.avg_monthly_invoiced)}
                sub={`${data.monthly.length} active month${data.monthly.length === 1 ? "" : "s"}`}
                hidden={financialsHidden}
                accent="blue"
              />
              <StatCard
                label="Avg / Month Net"
                value={fmtAUD(data.stats.avg_monthly_net)}
                sub="invoice − costs"
                hidden={financialsHidden}
                accent="emerald"
              />
              <StatCard
                label="Pipeline Value"
                value={fmtAUD(data.stats.pipeline_total)}
                sub={`${data.stats.pipeline_count} project${data.stats.pipeline_count === 1 ? "" : "s"} quoted, not invoiced`}
                hidden={financialsHidden}
                accent="violet"
              />
              <StatCard
                label="Completed"
                value={data.stats.completed_count}
                sub="all-time"
                hidden={false}
                accent="amber"
              />
            </div>

            {/* Chart */}
            <section className="bg-white border border-slate-200 rounded-sm p-5" data-testid="net-invoiced-chart">
              <header className="flex items-center gap-2 mb-3">
                <ChartLine size={14} className="text-emerald-500" weight="fill" />
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Net Invoiced Over Time</h3>
                <span className="text-[10px] text-slate-400 ml-1">(invoice − costs, completed projects, by completion month)</span>
              </header>
              {data.monthly.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-12 text-center">Complete a project to start seeing trends here.</p>
              ) : (
                <div className="h-72 w-full">
                  <ResponsiveContainer>
                    <AreaChart data={data.monthly} margin={{ top: 8, right: 20, bottom: 0, left: -8 }}>
                      <defs>
                        <linearGradient id="netGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#10b981" stopOpacity={0.35}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0.02}/>
                        </linearGradient>
                        <linearGradient id="invGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.20}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fill: "#64748b", fontSize: 11 }} />
                      <YAxis
                        tick={{ fill: "#64748b", fontSize: 11 }}
                        tickFormatter={(v) => financialsHidden ? "•••" : `$${(v / 1000).toFixed(0)}K`}
                      />
                      <Tooltip
                        labelFormatter={fmtMonth}
                        formatter={(value, name) => [financialsHidden ? "••••" : fmtAUDExact(value), name]}
                        contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 2 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="invoiced" name="Invoiced" stroke="#3b82f6" fill="url(#invGradient)" strokeWidth={2} />
                      <Area type="monotone" dataKey="net"      name="Net"      stroke="#10b981" fill="url(#netGradient)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>

            {/* Past projects table */}
            <PastProjectsTable
              rows={data.completed}
              hidden={financialsHidden}
              onUpdated={handleHoursUpdated}
              onEditCosts={handleOpenCostEdit}
            />

            {/* Hidden re-estimate trigger reachable from the table tooltip / detail page;
                also expose a "needs estimate" CTA when rows are missing AI hours. */}
            {data.completed.some((r) => r.estimated_hours == null) && (
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex items-center gap-3">
                <Sparkle size={14} className="text-blue-500 flex-shrink-0" weight="fill" />
                <p className="text-xs text-slate-700 flex-1">
                  Some completed projects don't have a work-hours estimate yet. Click <PencilSimple size={11} className="inline align-text-bottom" /> in the Hours column to enter your own, or:
                </p>
                {data.completed.filter((r) => r.estimated_hours == null).slice(0, 1).map((r) => (
                  <Button key={r.id} size="sm" variant="outline"
                    onClick={() => handleReEstimate(r)} data-testid={`re-estimate-${r.id}`}>
                    <Sparkle size={12} className="mr-1" /> Estimate "{r.name.substring(0, 20)}…"
                  </Button>
                ))}
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Quick cost-edit modal */}
      {editing && (
        <CostEditModal
          project={editing}
          onClose={() => setEditing(null)}
          onSave={handleSaveCosts}
        />
      )}
    </div>
  );
}
